import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router';

import styles from '../styles.module.css';
import { CompanyForm } from './companyForm';
import { companiesFormDataType, handleChangeCompaniesDataType } from './types';
import { updateCompanySchema } from './utils';
import { LocationsAssignedTable } from '../AssignsTables/locationsTable';
import { getLocationsRows } from '../AssignsTables/locationsTable';
import { UsersAssignedTable } from '../AssignsTables/usersTable';
import { LocationData as LocationDataType } from '../LocationsContainer/types';
import { filterLocalUsers } from '../LocationsContainer/utils';
import { AdminDeleteSection } from '../UtilsContainers/delete';
import { AdminDetailHeader } from '../UtilsContainers/header';
import ConfirmDeleteIcon from '@/assets/icons/confirm-delete.svg?react';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import CompanyIcon from '@/assets/icons/union.svg?react';
import { AdminCardButtons } from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import { getZodSchemaErrors } from '@/components/Form/Input/utils';
import { adminToolPaths } from '@/constants/adminTool';
import { companiesFormData } from '@/constants/companies';
import { PermissionsList } from '@/constants/permissionsList';
import { useModal } from '@/context/ModalContext';
import { useNavigationBar } from '@/context/NavigationBarContext';
import { useToast } from '@/context/ToastContext';
import {
  useDeleteCompany,
  useGetCompaniesInitial,
  useUpdateCompany,
  useGetUsersByCompany
} from '@/hooks/services/Companies';
import { useGetCompanyDetails } from '@/hooks/services/Companies';
import useGetLocationsList from '@/hooks/services/Locations';
import { companiesKey } from '@/solaris-lens-common-lib/src/constants/queryKeys/Companies';
import { RootState } from '@/store';
import { getUsersPermissions } from '@/utils/adminToolPermissions';

function CompanyDetails() {
  const navigate = useNavigate();
  const { setCustomBreadcrumbArray } = useNavigationBar();

  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;
  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { updateModalContent, closeModal, showTypedModal } = useModal();
  const { showToast } = useToast();
  const { state, pathname } = useLocation();
  const params = useParams<{ companyId: string }>();

  const { canEdit: canEditCompany, canDelete: canDeleteCompany } =
    getUsersPermissions(auditableElements, userGroup);

  const [showEditCompany, setShowEditCompany] = useState(false);
  const [formData, setFormData] =
    useState<companiesFormDataType>(companiesFormData);
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [onSubmit, setOnSubmit] = useState(false);

  const companyName = state?.companyName;
  const companyId =
    params.companyId ??
    matchPath(adminToolPaths.companies.detail, pathname)?.params?.companyId ??
    '';

  /*
    Validate company parameters and navigate to the company details page if the company name is found
  */
  const queryClient = useQueryClient();
  useEffect(() => {
    if (companyName) {
      queryClient.invalidateQueries({ queryKey: companiesKey.all });
    }
  }, [companyName, queryClient]);

  const { data: searchResponse } = useGetCompaniesInitial({
    searchParam: companyName ?? '',
    useQueryOptions: { enabled: !!companyName }
  });
  const searchCompanyId =
    companyName && searchResponse?.data?.[0]?.id
      ? searchResponse.data[0].id
      : null;

  useEffect(() => {
    if (searchCompanyId) {
      navigate(`${adminToolPaths.companies.root}/${searchCompanyId}`);
    }
  }, [searchCompanyId, navigate]);

  /*  Get Companies */
  const {
    data: companyProfile,
    isLoading: initialLoading,
    refetch
  } = useGetCompanyDetails({ companyId });

  useEffect(() => {
    if (companyProfile) {
      setFormData(companyProfile as companiesFormDataType);
      setCustomBreadcrumbArray({
        label: companyProfile.name || '',
        path: adminToolPaths.companies.detail.replace(
          ':companyId',
          companyProfile.id || ''
        )
      });
    }
  }, [companyProfile, setCustomBreadcrumbArray]);

  /* Get Users */
  const { data: usersData, isLoading: isLoadingUsers } = useGetUsersByCompany({
    companyName: companyProfile?.name || '',
    useQueryOptions: { enabled: !!companyProfile?.name }
  });
  const userRows = useMemo(
    () => filterLocalUsers(usersData ?? []),
    [usersData]
  );

  /* Get Locations */
  const { data: dataLocations, isLoading: isLoadingLocations } =
    useGetLocationsList(
      { pageSize: 100, method: 'firstPage', companyId: companyProfile?.id },
      {
        useQueryOptions: {
          enabled: !!companyProfile?.id,
          staleTime: 0,
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false
        }
      }
    );
  const locationRows = useMemo(
    () => getLocationsRows(dataLocations?.data || []),
    [dataLocations]
  );

  /*  Event Handlers */

  const setParsedZodData = (formData: companiesFormDataType) => {
    const parsed = updateCompanySchema.safeParse({
      ...formData
    });

    if (!parsed.success) {
      const errs = getZodSchemaErrors(parsed);
      setFormErrors(errs);
    } else {
      setFormErrors({});
    }

    return parsed;
  };

  const handleDelete = () => {
    deleteCompany(companyId || '', {
      onSuccess: () => {
        showTypedModal({
          type: 'info',
          title: 'Company was successfully deleted',
          icon: ConfirmDeleteIcon
        });
        setTimeout(() => {
          setShowEditCompany(false);
          closeModal();
          refetch();
          navigate(`${adminToolPaths.companies.root}`);
        }, 1500);
      }
    });
  };

  const handleChangeFormData: handleChangeCompaniesDataType = (
    field,
    value
  ) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    if (onSubmit) {
      setParsedZodData(newFormData);
    }
    setFormData(newFormData);
  };

  /*
   Companies actions
  */
  const {
    mutate: updateCompany,
    isPending,
    error: updateError,
    reset: updateReset
  } = useUpdateCompany();

  const {
    mutate: deleteCompany,
    isPending: isDeleting,
    error: deleteError,
    reset: deleteReset
  } = useDeleteCompany();

  const onUpdate = () => {
    setOnSubmit(true);
    const parsed = setParsedZodData(formData);

    const updatedData = {
      ...formData,
      phone:
        formData?.phone && formData?.phone?.startsWith('+1')
          ? formData.phone
          : formData.phone
            ? `+1${formData.phone}`
            : ''
    };

    if (parsed.success) {
      setFormErrors({});
      updateCompany(
        { companyId, companyData: updatedData },
        {
          onSuccess: () => {
            showTypedModal({
              type: 'success',
              title: 'Company was successfully updated'
            });
            setTimeout(() => {
              setShowEditCompany(false);
              closeModal();
              refetch();
            }, 2000);
          },
          onError: error => {
            console.error('Error saving company:', error);
          }
        }
      );
    }
  };

  const onDelete = () => {
    updateModalContent(null, {
      modalTitle: 'Delete',
      modalIcon: DeleteIcon,
      modalButtons: [
        {
          label: 'CANCEL',
          onClick: () => {
            closeModal();
          },
          variant: 'cancelRed'
        },
        {
          label: 'DELETE PERMANENTLY',
          onClick: () => {
            handleDelete();
          },
          variant: 'okRed',
          isLoader: isDeleting
        }
      ],
      alignButtons: 'center',
      contentTitle: 'You are about to delete',
      contentSubtitle: `${formData.name}`,
      contentDescription: (
        <div className={styles.contentDescription}>
          <span>This action can not be undone.</span>
          <br />
          <span>Are you sure you want to proceed?</span>
        </div>
      )
    });
    return;
  };

  const onCancel = () => {
    setOnSubmit(false);
    setFormErrors({});
    setFormData(companyProfile as companiesFormDataType);
    setShowEditCompany(false);
  };

  const onEditCompany = () => {
    setShowEditCompany(prev => !prev);
  };

  /* On assign actions */
  const onAssignUsers = async () => {};
  const onUnassignUser = async () => {};
  const onAssignLocations = async () => {};
  const onUnassignLocation = async () => {};

  const error = updateError || deleteError;
  const reset = updateReset || deleteReset;

  useEffect(() => {
    if (error) {
      showToast(error.message || 'An error occurred', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => {
          reset();
          refetch();
        }
      });
    }
  }, [error]);

  if (initialLoading) {
    return <ContainerLoader />;
  }

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminDetailHeader
          title={companyProfile?.name ?? ''}
          icon={CompanyIcon}
          isEditing={showEditCompany}
          onEditClick={canEditCompany ? onEditCompany : undefined}
          editButtonLabel="EDIT"
        />

        <CompanyForm
          formData={formData}
          handleChangeFormData={handleChangeFormData}
          showEditCompany={showEditCompany}
          formErrors={formErrors}
          container="edit"
        />
      </div>

      {!showEditCompany && (
        <UsersAssignedTable
          usersForTable={userRows}
          canAssignUnassignUsers={false}
          onAssignUsers={onAssignUsers}
          onUnassignUser={onUnassignUser}
          loading={isLoadingUsers}
        />
      )}

      {!showEditCompany && (
        <LocationsAssignedTable
          locationsForTable={locationRows}
          canAssignUnassignLocations={false}
          onAssignLocations={onAssignLocations}
          onUnassignLocation={onUnassignLocation}
          onRowClick={(row: LocationDataType) => {
            navigate(
              `${adminToolPaths.locations.detail.replace(':locationId', row.id)}`
            );
          }}
          loading={isLoadingLocations}
        />
      )}

      {showEditCompany && canDeleteCompany && (
        <AdminDeleteSection
          title="Delete Company"
          onDeleteClick={onDelete}
          text="This action can not be undone"
          buttonLabel="DELETE COMPANY PERMANENTLY"
          loading={isDeleting}
        />
      )}

      {showEditCompany && (
        <AdminCardButtons>
          <Button
            onClick={() => onCancel()}
            color="blue"
            solid={false}
            label="CANCEL"
            size="large"
          />
          <Button
            onClick={() => onUpdate()}
            color="blue"
            label="SAVE"
            size="large"
            loading={isPending}
          />
        </AdminCardButtons>
      )}
    </div>
  );
}

export default CompanyDetails;
